import React from 'react';
import { Link } from 'react-router-dom';
import AssessmentPage from './AssessmentPage';

const FasdAssessment = () => (
  <AssessmentPage
    seo={{
      title: 'FASD Assessment Coffs Harbour | Clinical Minds Psychology',
      description: 'Comprehensive FASD neurodevelopmental assessment in Coffs Harbour, aligned with the Australian assessment guidelines. Compassionate, thorough, judgement-free.',
      canonicalUrl: 'https://www.clinicalminds.net/fasd-assessment'
    }}
    heroTitle="FASD Assessment"
    heroSubtitle=""
    intro={
      <p className="body-medium">
        Fetal Alcohol Spectrum Disorder is one of the most under-recognised neurodevelopmental conditions in Australia, and one of the most misunderstood. Children with FASD are often labelled as naughty, defiant or lazy, when the truth is that their brain works differently and the world hasn't adjusted yet. A proper assessment replaces those labels with understanding. And to say it plainly: this is a judgement-free space. Assessment is about understanding a child's brain and getting them the right support, never about blame.
      </p>
    }
    whoFor={
      <p className="body-medium">
        Children and young people with confirmed or possible prenatal alcohol exposure where learning, behaviour, memory, attention or daily living skills are a struggle. I regularly work with birth parents, kinship and foster carers, adoptive families and caseworkers, and I'm experienced with the out-of-home-care context, where developmental history can be incomplete. Not knowing the full story is not a barrier to enquiring. I also assess adults.
      </p>
    }
    whatsInvolved={
      <p className="body-medium">
        FASD assessment is the most comprehensive assessment I offer. Aligned with the Australian Guide to the Assessment of FASD, it examines functioning across the neurodevelopmental domains: thinking and reasoning, memory, attention, language, motor skills, executive functioning, adaptive skills and more, using a battery of standardised assessment tools. I gather history from caregivers and school, and I work alongside your paediatrician or GP, since a FASD diagnosis draws on medical information as well as our neurodevelopmental findings.
      </p>
    }
    reportUsedFor={
      <p className="body-medium">
        The report maps the child's profile of strengths and difficulties across each domain, with recommendations written for the adults in the child's life: carers, teachers and support workers. It can support NDIS access, school learning adjustments and NCCD evidence, and care-team planning. For children in out-of-home care, a clear report often becomes the document that finally gets everyone on the same page.
      </p>
    }
    feesContent={
      <>
        <p className="body-medium">
          FASD assessment is our most comprehensive assessment and fees reflect its scope. See our <Link to="/fees" className="assess-link">fees page</Link> for information about funding options including the NDIS, or <Link to="/contact" className="assess-link">contact us</Link> and we'll talk it through.
        </p>
      </>
    }
    ctaText="Enquire about a FASD assessment"
    enquiryParam="fasd"
    faqs={[
      {
        question: "We can't confirm alcohol exposure during pregnancy. Can we still assess?",
        answer: "Yes. Unknown exposure is common, especially in out-of-home care. The assessment still builds a complete picture of the child's neurodevelopmental profile, and the report explains what conclusions can and can't be drawn."
      },
      {
        question: 'Will this assessment blame the birth mother?',
        answer: "No. FASD assessment done properly is compassionate to everyone involved. Many factors influence alcohol use in pregnancy, and our focus is entirely on understanding the child and putting support in place."
      },
      {
        question: 'How is FASD different from ADHD or autism?',
        answer: "The difficulties can look similar on the surface, which is why assessment examines each domain carefully. FASD, ADHD and autism can also co-occur, and the assessment is designed to untangle what's driving what."
      },
      {
        question: 'Does a caseworker or agency need to arrange this?',
        answer: "Enquiries can come from families, carers, caseworkers or agencies. We're used to working within care-team arrangements and can talk through consent and funding pathways with you."
      }
    ]}
  />
);

export default FasdAssessment;
